/**
 * Les réglages de dessin de la silhouette : gouttière, arrondi, courbe
 * d'opacité, couleurs de main.
 *
 * # Pourquoi un module à part
 *
 * Ils vivaient dans `piece-silhouette.tsx`, seul peintre de la figure. Il y en
 * a maintenant deux : le composant React, et `silhouette-svg.ts`, qui rend la
 * même figure en chaîne pour l'image d'aperçu d'un lien de partage — laquelle
 * est fabriquée hors du document, sans feuille de style à interroger.
 *
 * Deux peintres, un seul jeu de réglages : les recopier serait deux occasions
 * de diverger, et la figure d'un lien partagé ne ressemblerait plus à celle du
 * produit. Le module ne contient que des valeurs et une fonction pure — ni
 * React, ni DOM, ni SVG.
 *
 * Ce sont des décisions de **goût**, pas des invariants du format : elles vont
 * encore bouger, et c'est précisément pour ça qu'elles sont nommées et
 * groupées plutôt qu'enfouies dans une expression de dessin.
 */

import type { SilhouetteHand } from "./silhouette";

/**
 * Fond de la figure. C'est celui de la minimap du Studio
 * (`minimap-renderer.ts`), et ce n'est pas une coïncidence : les deux montrent
 * la même chose dans la même grammaire, elles doivent se reconnaître.
 */
export const SILHOUETTE_BACKGROUND = "#0c0c18";

/**
 * Espace entre deux cases, en unités du repère. Assez serré pour que les points
 * se touchent presque, ce qui donne une figure dense plutôt qu'un semis de
 * confettis.
 *
 * Un cran au-dessus du `1` de la minimap : depuis que la fiche dessine dans un
 * cadre aux proportions de la grille, la maille y est **carrée** et la marque
 * en occupe tout le côté moins ce jeu. À 1, les marques voisines se touchaient
 * et deux notes contiguës formaient un pâté ; à 1,2 la trame respire sans se
 * défaire. Mesuré en rendu réel, pas au calcul.
 */
export const CELL_GAP = 1.2;

/**
 * Arrondi des marques : léger, juste de quoi casser l'angle. Divergence
 * assumée d'avec `minimap-renderer.ts` et son `roundRect(..., 1.5)` — on lui
 * emprunte l'algorithme et la palette, pas son arrondi. À la taille d'une
 * vignette de catalogue, 1,5 arrondit trop et le carré tourne à la pastille.
 * Ce n'est pas un oubli à « réaligner » sur la minimap dans six mois.
 */
export const CELL_RADIUS = 1;

/**
 * Une règle horizontale tous les N rangs.
 *
 * Elles ne portent aucune information musicale — l'axe des hauteurs n'est pas
 * gradué et ne peut pas l'être, la grille étant normalisée par morceau. Elles
 * donnent une **échelle** au cadre : sans elles, la moitié du catalogue rend
 * une poignée de points perdus dans un rectangle noir, ce qui se lit comme un
 * rendu raté plutôt que comme « ce morceau ne bouge pas ».
 */
export const RULE_ROW_STEP = 4;
export const RULE_COLOR = "rgba(255, 255, 255, 0.05)";
export const RULE_THICKNESS = 1;

/**
 * Courbe d'opacité, appliquée à la densité `]0, 1]` d'une case.
 *
 * Ce n'est **pas** la courbe de la minimap (`0.3 + d × 0.6`), et l'écart est
 * délibéré. Sur une grille grossière (28 × 13), les morceaux nourris occupent
 * jusqu'à la moitié des cases — mesuré sur les 221 pièces du catalogue de dev.
 * Avec un plancher à 0,3, toutes ces cases se ressemblent et le contour du
 * morceau se noie dans un mur uniforme.
 *
 * Le plancher très bas efface presque les cases faibles, et l'exposant < 1
 * fait monter vite les cases moyennes : le cœur du morceau ressort, la
 * périphérie s'estompe, et la figure retrouve une forme reconnaissable.
 */
const ALPHA_FLOOR = 0.12;
const ALPHA_RANGE = 0.88;
const ALPHA_EXPONENT = 0.7;

export function cellAlpha(density: number): number {
  return ALPHA_FLOOR + Math.pow(density, ALPHA_EXPONENT) * ALPHA_RANGE;
}

/**
 * Les couleurs de main du produit, les mêmes que dans le Studio. Quelqu'un qui
 * a déjà joué un morceau reconnaît la main gauche à sa couleur avant de lire
 * quoi que ce soit.
 */
export const HAND_FILL: Record<SilhouetteHand, string> = {
  left: "var(--color-hand-left)",
  right: "var(--color-hand-right)",
};

/**
 * Les mêmes couleurs, écrites en clair.
 *
 * Une figure rendue **hors du document** — l'image d'aperçu d'un lien de
 * partage, fabriquée côté serveur puis rasterisée — n'a aucune feuille de
 * style où résoudre `var(--color-hand-left)` : les jetons y donneraient des
 * cases noires. Les valeurs sont recopiées de `globals.css`, qui reste la
 * référence : ce doublon-là est le prix d'un rendu sans document, et il est
 * signalé des deux côtés.
 */
export const HAND_FILL_LITERAL: Record<SilhouetteHand, string> = {
  left: "#2FD7D6",
  right: "#B47CFF",
};

/** Le territoire de chaque main. Voir `handTerritory()` pour le raisonnement. */
export const HAND_TERRITORY_FILL: Record<SilhouetteHand, string> = {
  left: "var(--color-hand-left-territory)",
  right: "var(--color-hand-right-territory)",
};

/**
 * L'ordre de peinture des mains, et il compte : les deux mains partagent la
 * grille, donc elles se recouvrent case par case dès qu'elles jouent la même
 * hauteur au même moment. La gauche passe en dernier parce que c'est la plus
 * discrète des deux — la droite chante, la gauche tient — et que c'est
 * précisément sa ligne continue qui dit la structure du morceau. Peinte
 * dessous, elle disparaîtrait sous la mélodie.
 */
export const PAINT_ORDER: readonly SilhouetteHand[] = ["right", "left"];
